import { api } from '@/lib/axios';
import { API_ROUTES } from '@/config/constants';
import { normalizeRoles } from '@/utils/normalize-role';
import type { Admin, CurrentUserDto, WorkspaceDto } from '@/types/auth';
import type { CredentialRedeemResponse, RedeemCredentialPayload } from '@/types/auth-credential';

const splitDisplayName = (name: string): { firstName: string; lastName: string } => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return { firstName: parts[0] ?? name, lastName: parts.slice(1).join(' ') };
};

/**
 * The real backend has no single "profile" endpoint — this composes one from three calls:
 * GET /me (identity), GET /me/workspaces (wholesaler memberships). `Admin` is assembled to
 * match what the rest of the app already expects, so downstream consumers need no changes.
 *
 * Lives in lib/ (not features/auth/api/) because it is consumed by the shared hooks
 * `useAuth` and `useBootstrapAuth` in src/hooks/, which cannot import from src/features/.
 */
export const fetchProfile = async (): Promise<Admin> => {
  const [{ data: me }, { data: workspaces }] = await Promise.all([
    api.get<CurrentUserDto>(API_ROUTES.auth.me),
    api.get<WorkspaceDto[]>(API_ROUTES.auth.meWorkspaces),
  ]);
  const { firstName, lastName } = splitDisplayName(me.name);

  return {
    id: me.subject,
    email: me.email,
    displayName: me.name,
    firstName,
    lastName,
    phone: null,
    lastLogin: null,
    loginFailures: 0,
    status: 'active',
    roles: normalizeRoles(me.roles),
    wholesalers: workspaces,
    profileSetupComplete: workspaces.length > 0,
  };
};

/**
 * Ask the backend to email a sign-in credential — a link plus a 6-digit code.
 *
 * Answers 200 whether or not the address has an account, and 429 when the per-address issuance
 * throttle is tripped. The 429 fires identically for known and unknown addresses, so neither
 * outcome may be surfaced as "that account exists".
 *
 * Lives in lib/ rather than a feature `api/` folder because both the landing sign-in card and the
 * /signin route need it, and Rule 2 forbids one feature importing another's api module.
 */
export const requestSignInCode = async (payload: {
  email: string;
}): Promise<{ success: boolean }> => {
  const { data } = await api.post<{ success: boolean }>(API_ROUTES.auth.signInRequest, payload);
  return data;
};

/**
 * Spend a sign-in credential, by hotlink token or by email + code, for a full session token.
 * A rejected credential comes back as a 200 with `success: false` — see
 * `CredentialRedeemResponse`.
 */
export const redeemSignInCredential = async (
  payload: RedeemCredentialPayload,
): Promise<CredentialRedeemResponse> => {
  const { data } = await api.post<CredentialRedeemResponse>(API_ROUTES.auth.signInRedeem, payload);
  return data;
};
